import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import Toolbar, { Item as ToolbarItem } from 'devextreme-react/toolbar';
import Tabs, { Item as TabItem } from 'devextreme-react/tabs';
import Box, { Item as BoxItem } from 'devextreme-react/box';
import Button from 'devextreme-react/button';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import ButtonGroup from 'devextreme-react/button-group';
import stringify from '../../../utils/stableStringify';

import './index.scss';
import { AntGlobal, AntSession } from '../../../utils/AntContext';
import { useNavigate } from 'react-router-dom';

import AntLocalization from '../../../utils/AntLocalization';
import AntCondition from '../../../utils/AntCondition';
import AntAPI from '../../../utils/AntAPI';
import AntAuth from '../../../utils/AntAuth';
import cs from '../../../utils/const';
import { ImExit } from 'react-icons/im';
import { BiLogOut } from 'react-icons/bi';
import { fitOrdersToWidth, getAvailableTabsWidth, getOrderKey, limitOrdersKeepingActive } from './tabLayout';

const ORDER_TAB_GAP = 6;
const ORDER_TAB_HORIZONTAL_PADDING = 20;
const ORDER_TAB_QUERY_SLOT_WIDTH = 72;
const CREATE_TAB_NON_TEXT_WIDTH = 46;
const TABS_EXISTING_RIGHT_MARGIN = 20;
const MAX_ORDER_QUERY_PAGE_SIZE = 20;

export default forwardRef(function (props, ref) {
    const navigate = useNavigate();
    const appGlobal = AntGlobal();
    const session = AntSession();

    const API = useMemo(() => (props.API ? props.API : props.module && props.table ? new AntAPI(props.module, props.table) : undefined), []);

    const selectFields = useMemo(() => (props.selectFields ? props.selectFields : ['code']), []);
    const displayField = useMemo(() => (props.displayField ? props.displayField : 'code'), []);
    const displayFrefix = useMemo(() => (props.displayFrefix ? props.displayFrefix : ''), []);

    var title = appGlobal.appInfo && appGlobal.appInfo.title ? appGlobal.appInfo.title : '';
    var logoOnly = appGlobal.appAssets && appGlobal.appAssets.images.logoOnly ? appGlobal.appAssets.images.logoOnly : undefined;
    var logoWithBrand = appGlobal.appAssets && appGlobal.appAssets.images.logoWithBrand ? appGlobal.appAssets.images.logoWithBrand : undefined;
    var iconDashBoard = appGlobal.appAssets && appGlobal.appAssets.icons.icon_dashboard ? appGlobal.appAssets.icons.icon_dashboard : undefined;

    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const headerLoadVersion = useRef(0);
    const [adding, setAdding] = useState(false);
    const textMeasureCanvas = useRef(null);
    // const [selectedOrderIndex, setSelectedOrdeIndex] = useState(0);

    // The create tab must not depend on an asynchronously loaded order list.
    // In particular, a user opening POS with no active order must still be
    // able to create one when their configured permission allows it.
    const isCreateTabVisible = useMemo(
        () => props.showAdd !== undefined && AntCondition.isVisible(null, props.showAdd),
        [props.showAdd]
    );

    const createTabText = AntLocalization.translateText({ vi: 'Tạo đơn', en: 'Create order', fr: 'Créer commande', es: 'Crear pedido', id: 'Buat pesanan', ja: '注文作成', zh: '创建订单', ko: '주문 생성', hi: 'ऑर्डर बनाएँ', ar: 'إنشاء طلب' });

    const getTabText = useCallback(
        (data) => {
            const displayValue = data?.[displayField] ?? data?.code ?? '';
            let text = `${displayFrefix}${displayValue}`;

            if (props.onCustomText) {
                const customText = props.onCustomText(data, text);
                if (customText !== undefined && customText !== null) text = customText;
            }

            return text;
        },
        [displayField, displayFrefix, props.onCustomText]
    );

    const measureText = useCallback((value, fontSize, fontWeight = 400) => {
        if (typeof value !== 'string' && typeof value !== 'number') return ORDER_TAB_QUERY_SLOT_WIDTH;
        if (typeof document === 'undefined') return String(value).length * fontSize * 0.62;

        if (!textMeasureCanvas.current) textMeasureCanvas.current = document.createElement('canvas');
        const context = textMeasureCanvas.current.getContext('2d');
        if (!context) return String(value).length * fontSize * 0.62;

        const fontFamily = window.getComputedStyle(document.body).fontFamily || 'Arial, sans-serif';
        context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        return context.measureText(String(value)).width;
    }, []);

    const createTabWidth = useMemo(
        () => Math.ceil(measureText(createTabText, 14, 500)) + CREATE_TAB_NON_TEXT_WIDTH,
        [createTabText, measureText]
    );

    const availableTabsWidth = getAvailableTabsWidth(
        props.rightPanelWidth,
        props.headerRightWidth,
        TABS_EXISTING_RIGHT_MARGIN
    );
    const estimatedOrderCapacity = useMemo(() => {
        const createWidth = isCreateTabVisible ? createTabWidth + ORDER_TAB_GAP : 0;
        const remainingWidth = Math.max(0, availableTabsWidth - createWidth);
        return Math.max(1, Math.floor((remainingWidth + ORDER_TAB_GAP) / (ORDER_TAB_QUERY_SLOT_WIDTH + ORDER_TAB_GAP)));
    }, [availableTabsWidth, createTabWidth, isCreateTabVisible]);
    const orderQueryPageSize = Math.min(MAX_ORDER_QUERY_PAGE_SIZE, Math.max(4, estimatedOrderCapacity));

    const headerOrders = useMemo(() => {
        const dataOrders = orders.filter((item) => item?.type !== 'add' && (item?.id || item?.code));
        const activeKey = getOrderKey(props.mainData) || getOrderKey(selectedOrder);
        const visibleOrders = fitOrdersToWidth({
            orders: dataOrders,
            activeKey,
            availableWidth: availableTabsWidth,
            reservedWidth: isCreateTabVisible ? createTabWidth : 0,
            gapWidth: ORDER_TAB_GAP,
            getItemWidth: (item, isActive) => {
                const fontSize = isActive ? 15 : 13;
                return Math.ceil(measureText(getTabText(item), fontSize)) + ORDER_TAB_HORIZONTAL_PADDING;
            }
        });
        return isCreateTabVisible ? [...visibleOrders, { id: 'add', type: 'add' }] : visibleOrders;
    }, [orders, selectedOrder, props.mainData, isCreateTabVisible, createTabWidth, availableTabsWidth, measureText, getTabText]);

    const isPOSOnly = useMemo(() => {
        let isValid = AntCondition.isValid(null, {
            whenUser: {
                UI_display_mode: 'POS_ONLY'
            }
        });
        if (isValid) {
            var userGroupCode = AntAuth.getSessionInfo('user_group_code');
            if (userGroupCode === 'Admin') isValid = false;
        }

        return isValid;
    }, []);

    const homeURL = useMemo(() => {
        if (session?.getHomeURL) return session?.getHomeURL();
        return [];
    }, [session]);

    const routes = useMemo(() => {
        if (session?.getRoutes) return session?.getRoutes();
        return [];
    }, [session]);

    const headerBox = useRef();

    const onBackToHome = useCallback(() => {
        navigate('/pos/home');
    }, []);

    // const onBackToHome = useCallback(() => {
    //     var uiData = localStorage.getItem(cs.System_Code + '-ui-states');
    //     if (uiData) uiData = JSON.parse(uiData);
    //     if (!uiData) uiData = {};

    //     localStorage.setItem(
    //         cs.System_Code + '-ui-states',
    //         String(
    //             stringify({
    //                 ...uiData,
    //                 mode: 'WebUI'
    //             })
    //         )
    //     );
    //     navigate('/ar/AROrders');
    //     window.location.reload();
    // }, []);

    const tabItemRender = useCallback(
        (data) => {
            if (data?.type === 'add')
                return (
                    <div className={adding ? 'ant-pos-tab-create ant-pos-tab-create-disabled' : 'ant-pos-tab-create'} aria-disabled={adding}>
                        <LoadIndicator className="ant-pos-tab-create-spinner" visible={Boolean(adding)} />
                        {!adding && <i className="dx-icon dx-icon-plus" />}
                        <span>{createTabText}</span>
                    </div>
                );

            const strText = getTabText(data);

            return (
                <div
                    className={Boolean(data?.isClicked) ? 'ant-pos-tab-item ant-pos-tab-item-focused' : 'ant-pos-tab-item'}
                    title={typeof strText === 'string' || typeof strText === 'number' ? String(strText) : undefined}
                >
                    {strText}
                </div>
            );
        },
        [adding, createTabText, getTabText]
    );

    const onItemClick = useCallback(
        (e) => {
            let item = e.itemData;
            if (item?.type === 'add') {
                if (adding) return;
                if (props.onActionCreate) {
                    setAdding(true);
                    props.onActionCreate(() => setAdding(false));
                }
            } else {
                setSelectedOrder(item);
                // setSelectedOrdeIndex(orders.findIndex((o) => o.id === item?.id));
            }
        },
        [adding, props.onActionCreate]
    );

    const loadData = useCallback(async (activeOrder = props.mainData) => {
        if (props.hideTabs) return [];

        // Keep the legacy header query for every POS voucher except the order
        // workspace that explicitly opts into the recent-draft order tabs.
        if (!props.includeRecentDraftOrders) {
            const result = await API.getList({
                ...props.searchConditions,
                isSearching: true,
                isSearchVoucherTabs: props.isSearchVoucherTabs,
                selectFields: selectFields
            });
            const rows = result?.rows instanceof Array ? result.rows : [];
            const legacyOrders = rows.slice(0, orderQueryPageSize);
            setOrders(legacyOrders);
            return legacyOrders;
        }
        const loadVersion = ++headerLoadVersion.current;
        const currentOrder = activeOrder?.id || activeOrder?.code ? activeOrder : null;
        const initialOrders = currentOrder ? [currentOrder] : [];
        const shouldPrioritizeInitialOrder =
            Boolean(currentOrder?.serve_place_code || currentOrder?.serve_place_name) || currentOrder?.sale_mode === 'TAKE_AWAY';
        if (shouldPrioritizeInitialOrder || orders.length === 0) {
            setOrders(initialOrders);
        }

        const servePlaceCode = props.searchConditions?.serve_place_code;
        if (servePlaceCode) {
            const relatedSearch = {
                order_status: ['New', 'Release', 'Open'],
                orderBy: 'released_at',
                orderType: 'desc',
                pageSize: orderQueryPageSize,
                isSearching: true,
                isSearchVoucherTabs: props.isSearchVoucherTabs,
                selectFields
            };
            // New orders keep all assigned places in the JSON array, while old
            // records may only have the scalar column. Keep both bounded queries
            // until legacy data is migrated, then merge by order code below.
            const [multipleResult, legacyResult] = await Promise.all([
                API.getList({
                    ...relatedSearch,
                    serve_place_codes: [servePlaceCode],
                    serve_place_codes_search_inner_join: true
                }, null, { byPassTotal: true }),
                API.getList({ ...relatedSearch, serve_place_code: servePlaceCode }, null, { byPassTotal: true })
            ]);
            if (loadVersion !== headerLoadVersion.current) return [];
            const relatedByCode = new Map();
            [multipleResult?.rows, legacyResult?.rows].flat().filter(Boolean).forEach((row) => {
                if (row?.code) relatedByCode.set(row.code, row);
            });
            // Do not spread Map iterators here. The kit bundler transpiles that
            // expression to [].concat(map.values()), leaving the iterator itself
            // as a tab item instead of expanding its order rows.
            const relatedOrders = Array.from(relatedByCode.values())
                .filter((row) => row && (row.id || row.code))
                .sort(
                    (left, right) =>
                        Number(right?.released_at || right?.created_at || right?.id || 0) -
                        Number(left?.released_at || left?.created_at || left?.id || 0)
                );
            const currentKey = getOrderKey(currentOrder);
            const relatedWithoutCurrent = currentKey ? relatedOrders.filter((row) => getOrderKey(row) !== currentKey) : relatedOrders;
            const candidates = currentOrder ? [currentOrder, ...relatedWithoutCurrent] : relatedWithoutCurrent;
            const nextOrders = candidates.slice(0, orderQueryPageSize);
            setOrders(nextOrders);
            return nextOrders;
        }

        const result = await API.getList(
            {
                serve_place_code: [null],
                sale_mode: { type: '!=', value: 'TAKE_AWAY' },
                order_status: ['New'],
                orderBy: 'created_at',
                orderType: 'desc',
                pageSize: orderQueryPageSize,
                isSearching: true,
                isSearchVoucherTabs: props.isSearchVoucherTabs,
                selectFields: selectFields
            },
            null,
            { byPassTotal: true }
        );
        if (loadVersion !== headerLoadVersion.current) return [];

        const currentOrderKey = getOrderKey(currentOrder);
        const draftOrders = (result?.rows instanceof Array ? result.rows : [])
            .filter(
                (row) =>
                    row?.order_status === 'New' &&
                    !row?.serve_place_code &&
                    !row?.serve_place_name &&
                    row?.sale_mode !== 'TAKE_AWAY'
            )
            .slice(0, orderQueryPageSize);
        const isCurrentOrderInDrafts = currentOrderKey && draftOrders.some((row) => getOrderKey(row) === currentOrderKey);
        const shouldPrioritizeCurrentOrder =
            Boolean(currentOrder?.serve_place_code || currentOrder?.serve_place_name) || currentOrder?.sale_mode === 'TAKE_AWAY';
        const candidateOrders = isCurrentOrderInDrafts
            ? draftOrders
            : currentOrder
            ? shouldPrioritizeCurrentOrder
                ? [currentOrder, ...draftOrders]
                : [...draftOrders, currentOrder]
            : draftOrders;
        const nextOrders = limitOrdersKeepingActive(candidateOrders, currentOrder, orderQueryPageSize);
        setOrders(nextOrders);
        return nextOrders;
    }, [
        props.mainData,
        props.includeRecentDraftOrders,
        props.isSearchVoucherTabs,
        props.searchConditions,
        orders.length,
        orderQueryPageSize,
        selectFields,
        API
    ]);

    // useEffect(() => {
    //     loadData();
    // }, []);

    const autoFocus = useCallback(
        (newOrders) => {
            let rOrders = newOrders?.filter((o) => o.type !== 'add');
            if (rOrders) {
                let rOrder = null;
                if (props.mainData) {
                    rOrder = newOrders?.find((o) => o.code === props.mainData.code || o.id === props.mainData.id);
                } else {
                    rOrder = rOrders && rOrders.length > 0 ? rOrders[0] : null;
                }

                if (!selectedOrder || (rOrder && rOrder.code !== selectedOrder.code && rOrder.id !== selectedOrder.id)) {
                    setSelectedOrder(rOrder);
                }
            }
        },
        [selectedOrder, props.mainData]
    );

    const onSelected = useCallback(
        (data) => {
            if (!props.mainData || !data || (props.mainData?.code !== data?.code && props.mainData?.id !== data?.id)) {
                if (props.onMainCodeChanged) props.onMainCodeChanged(data);
            }
        },
        [props.mainData, props.onMainCodeChanged]
    );

    const onMainChanged = useCallback(
        async (data) => {
            if (!data?.id && !data?.code) return;
            // Opening a table/order can keep the previous selected tab alive while
            // its voucher code changes. Always reload from the newly opened order.
            const newOrders = await loadData(data);
            const activeHeaderOrder = newOrders.find((row) => row?.code === data?.code || row?.id === data?.id);
            setSelectedOrder(activeHeaderOrder || data);
        },
        [loadData]
    );

    const reload = useCallback(async () => {
        if (!props.includeRecentDraftOrders) {
            if (props.onMainCodeChanged) props.onMainCodeChanged(null);
            return;
        }
        const recentDraftOrders = await loadData(null);
        const nextOrder = recentDraftOrders.find((row) => row?.type !== 'add' && (row?.id || row?.code));
        if (!nextOrder) return props.onMainCodeChanged?.(null);
        await loadData(nextOrder);
        props.onMainCodeChanged?.(nextOrder);
    }, [loadData, props.includeRecentDraftOrders, props.onMainCodeChanged]);

    useEffect(() => {
        autoFocus(orders);
    }, [orders]);

    useEffect(() => {
        if (selectedOrder) onSelected(selectedOrder);
    }, [selectedOrder]);

    useEffect(() => {
        onMainChanged(props.mainData);
    }, [props.mainData, props.searchConditions, orderQueryPageSize]);

    useImperativeHandle(ref, () => ({
        reload: reload
    }));

    useEffect(() => {
        if (headerBox.current) {
            headerBox.current.instance.repaint();
        }
    }, [props.posLayoutSizes]);
    
    const onClickLogOut = useCallback( () => {
        if (isPOSOnly) {
            AntAuth.logOut();
            return;
        }
        var uiData = localStorage.getItem(cs.System_Code + '-ui-states');
        if (uiData) uiData = JSON.parse(uiData);
        if (!uiData) uiData = {};

        localStorage.setItem(
            cs.System_Code + '-ui-states',
            String(
                stringify({
                    ...uiData,
                    mode: 'WebUI'
                })
            )
        );
        AntAuth.refreshToken(false, null, () => {
            window.location.reload();
        });
    }, [isPOSOnly]);

    return (
        <header className={'ant-pos-header'}>
            <Box
                ref={headerBox}
                direction="row"
                width="100%"
                height="100%"
                elementAttr={{
                    class: 'ant-pos-screen-header'
                }}
            >
                <BoxItem ratio={props.posLayoutSizes[0]}>
                    <Toolbar className={'ant-pos-header-left-toolbar'}>
                        {/* {appGlobal.appInfo.title && <ToolbarItem location={'before'} className={'ant-pos-header-title'} text={appGlobal.appInfo.title} visible={!!appGlobal.appInfo.title} />} */}

                        <ToolbarItem location={'before'}>
                            <Button
                                className={'ant-back-button'}
                                icon={'arrowleft'}
                                type={'normal'}
                                stylingMode="text"
                                activeStateEnabled={false}
                                focusStateEnabled={false}
                                hoverStateEnabled={false}
                                onClick={onBackToHome}
                                style={{
                                    display: homeURL === '/pos/home' ? 'block' : 'none'
                                }}
                            ></Button>

                            <Button
                                className={'ant-logout-button'}
                                // icon={'clearcircle'}
                                // type={'normal'}
                                stylingMode="text"
                                activeStateEnabled={false}
                                focusStateEnabled={false}
                                hoverStateEnabled={false}
                                onClick={onClickLogOut}
                                style={{
                                    display: homeURL !== '/pos/home' ? 'block' : 'none'
                                }}
                            >
                                <div className={'pos-user-action'}>
                                    <BiLogOut className={'ant-logout-button-icon'} />
                                </div>
                            </Button>
                        </ToolbarItem>
                        {props.title && (
                            <ToolbarItem
                                location={'before'}
                                template={() => {
                                    return `<h4 class='ant-screen-title'>${AntLocalization.translateText(props.title)}</h4>`;
                                }}
                            ></ToolbarItem>
                        )}
                        {props.headerLeftExtras && (
                            <ToolbarItem location={'before'}>
                                {props.headerLeftExtras}
                            </ToolbarItem>
                        )}
                        {/* {logoWithBrand && (
                    <ToolbarItem location={'before'}>
                        <Button type={'normal'} stylingMode="text" activeStateEnabled={false} focusStateEnabled={false} hoverStateEnabled={false}>
                            {logoOnly && <img src={logoOnly} style={{ width: '24px', marginLeft: '5px' }} />}{' '}
                            {logoWithBrand && <img src={logoWithBrand} style={{ width: '60px'}} />}
                        </Button>
                    </ToolbarItem>
                )} */}
                        {/* <ToolbarItem location={'center'}>
                    <ButtonGroup
                        items={[
                            {
                                icon: 'bold',
                                style: 'bold'
                            },
                            {
                                icon: 'italic',
                                style: 'italic'
                            },
                            {
                                icon: 'underline',
                                style: 'underline'
                            },
                            {
                                icon: 'strike',
                                style: 'strike'
                            }
                        ]}
                        keyExpr="style"
                    />
                </ToolbarItem> */}
                    </Toolbar>
                </BoxItem>
                <BoxItem ratio={props.posLayoutSizes[1]}>
                    {!props.hideTabs && (
                        <Tabs
                            className={'ant-pos-header-tabs-orders'}
                            itemRender={tabItemRender}
                            selectedItem={selectedOrder}
                            noDataText={''}
                            // animationEnabled={true}
                            // swipeEnabled={true}
                            // selectedIndex={orders.findIndex((o) => o.id === selectedOrder?.id)}
                            // onSelectionChanged={onSelectionChanged}
                            onItemClick={onItemClick}
                            items={headerOrders}
                        />
                    )}
                </BoxItem>
            </Box>
        </header>
    );
});
